import { collection, doc, setDoc, getDocs, query, where, orderBy, Timestamp, deleteDoc } from 'firebase/firestore';
import Firebase from '../components/Firebase/Firebase';
import { useChatbotStore } from '../store/chatbot.store';
import { API_CONFIG, AI_CONFIG } from '../config';

export type TagCategory = 'moods' | 'instruments' | 'energy' | 'period' | 'style' | 'location' | 'other';

export interface TagMapping {
  tag: string;
  category: TagCategory;
  confidence?: number;
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  source: 'gemini' | 'manual' | 'admin';
}

export interface PendingTagMapping extends TagMapping {
  status: 'pending' | 'approved' | 'rejected';
}

class TagMappingService {
  private readonly API_BASE_URL = API_CONFIG.GEMINI.ENDPOINT;
  private cache: Map<string, TagCategory> = new Map();
  private readonly MAPPINGS_COLLECTION = 'mappings';
  private readonly TAG_MAPPINGS_SUBCOLLECTION = 'tagMappings';
  private readonly PENDING_MAPPINGS_SUBCOLLECTION = 'pendingTagMappings';
  private lastCacheUpdate: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private firebase: Firebase;

  constructor() {
    this.firebase = new Firebase();
    // Don't auto-load - cache will be loaded on first use when user is authenticated
    // this.loadMappingsToCache();
  }


  // Load all approved mappings from Firestore to cache
  async loadMappingsToCache(force: boolean = false): Promise<void> {
    const now = Date.now();
    
    // Check if cache is still fresh
    if (!force && this.cache.size > 0 && (now - this.lastCacheUpdate) < this.CACHE_DURATION) {
      return;
    }

    try {
      // Reference the subcollection: mappings/config/tagMappings
      const mappingsRef = collection(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION);
      const snapshot = await getDocs(mappingsRef);
      
      this.cache.clear();
      snapshot.forEach((doc) => {
        const data = doc.data() as TagMapping;
        this.cache.set(data.tag.toLowerCase(), data.category);
      });
      
      this.lastCacheUpdate = now;
    } catch (error) {
      console.error('Error loading tag mappings:', error);
    }
  }

  // Get category for a single tag from cache
  getCategoryFromCache(tag: string): TagCategory | null {
    return this.cache.get(tag.toLowerCase()) || null;
  }

  // Categorize multiple tags using cache
  categorizeTags(tags: string[]): Record<TagCategory, string[]> {
    const result: Record<TagCategory, string[]> = {
      moods: [],
      instruments: [],
      energy: [],
      period: [],
      style: [],
      location: [],
      other: []
    };

    tags.forEach(tag => {
      const category = this.getCategoryFromCache(tag) || 'other';
      result[category].push(tag);
    });

    return result;
  }

  // Use Gemini to categorize new unmapped tags in batches
  async categorizeWithGemini(tags: string[]): Promise<PendingTagMapping[]> {
    const { config } = useChatbotStore.getState();
    
    if (!config || !config.apiKey || !config.enabled) {
      throw new Error('Chatbot is not configured or enabled');
    }

    // Process in smaller batches to avoid truncation
    const BATCH_SIZE = 20;
    const allResults: PendingTagMapping[] = [];
    
    for (let i = 0; i < tags.length; i += BATCH_SIZE) {
      const batch = tags.slice(i, i + BATCH_SIZE);
      
      const batchResults = await this.processBatch(batch, config);
      allResults.push(...batchResults);
      
      // Small delay between batches to be nice to the API
      if (i + BATCH_SIZE < tags.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return allResults;
  }

  private async processBatch(tags: string[], config: any): Promise<PendingTagMapping[]> {
    const prompt = `You are a music tag categorization expert. Categorize ONLY the following ${tags.length} audio/music tags into EXACTLY one of these 6 categories:

Categories:
- moods: emotional states, feelings, atmosphere (happy, sad, dark, uplifting, emotive, inspirational, feel good, calm, etc.)
- instruments: musical instruments, sound sources (guitar, piano, synth, drums, bass, percussion, strings, brass, vocals, etc.)
- energy: tempo, intensity, dynamics (fast, slow, energetic, calm, driving, mellow, intense, etc.)
- period: time periods, eras, age-related (80s, 90s, retro, modern, vintage, contemporary, classic, etc.)
- style: music genres, styles, production styles (rock, pop, jazz, electronic, hip-hop, orchestral, cinematic, acoustic, etc.)
- location: geographical, cultural, regional references (African, European, Asian, American, Latin, Eastern, Western, etc.)

If a tag doesn't clearly fit any category, use "moods" as the default.

IMPORTANT: You must return EXACTLY ${tags.length} mappings, one for each of these tags: ${tags.join(', ')}

Do not categorize any other tags. Do not add extra mappings.

Return ONLY a valid JSON array with exactly ${tags.length} objects like this example:
[
  {"tag": "Piano", "category": "instruments", "confidence": 0.95},
  {"tag": "Feel Good", "category": "moods", "confidence": 0.90}
]

Do not include any other text. Return only the JSON array with exactly ${tags.length} mappings.`;

    try {
      const response = await fetch(`${this.API_BASE_URL}?key=${config.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [{ text: prompt }]
            }
          ],
          generationConfig: AI_CONFIG.GEMINI.GENERATION_CONFIG
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Gemini API Error:', errorData);
        throw new Error(`API error: ${errorData.error?.message || 'Unknown error'}`);
      }
      
      const data = await response.json();
      
      // Extract the JSON from the response
      const textResponse = data.candidates[0].content.parts[0].text;
      
      // Remove markdown code block markers if present
      let cleanedResponse = textResponse.replace(/```json\s*/g, '').replace(/```\s*/g, '');
      
      // Handle incomplete JSON by trying to fix it
      let jsonMatch = cleanedResponse.match(/\[[\s\S]*\]/);
      
      if (!jsonMatch) {
        // Try to find partial array and fix it
        const partialMatch = cleanedResponse.match(/\[[\s\S]*/);
        if (partialMatch) {
          let partialJson = partialMatch[0];
          
          // Count braces and try to complete the JSON
          const openBraces = (partialJson.match(/\{/g) || []).length;
          const closeBraces = (partialJson.match(/\}/g) || []).length;
          const missingBraces = openBraces - closeBraces;
          
          // Add missing closing braces and bracket
          partialJson += '}'.repeat(missingBraces);
          if (!partialJson.trim().endsWith(']')) {
            partialJson += ']';
          }
          
          jsonMatch = [partialJson];
        } else {
          throw new Error(`No JSON found in batch response`);
        }
      }
      
      let categorized;
      try {
        categorized = JSON.parse(jsonMatch[0]);
        
        // Ensure it's an array
        if (!Array.isArray(categorized)) {
          categorized = [categorized];
        }
      } catch (parseError) {
        // Fallback: create basic mappings for this batch
        return tags.map(tag => ({
          tag,
          category: 'moods' as TagCategory, // Default fallback
          confidence: 0.5,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          source: 'gemini' as const,
          status: 'pending' as const
        }));
      }
      
      // Create a mapping from Gemini's returned tags to our requested tags
      const geminiToRequestedMap = new Map<string, string>();
      
      categorized.forEach((item: any) => {
        const geminiTag = item.tag;
        
        // Try exact match first
        const exactMatch = tags.find(tag => tag === geminiTag);
        if (exactMatch) {
          geminiToRequestedMap.set(geminiTag, exactMatch);
          return;
        }
        
        // Try case-insensitive match
        const caseMatch = tags.find(tag => tag.toLowerCase() === geminiTag.toLowerCase());
        if (caseMatch) {
          geminiToRequestedMap.set(geminiTag, caseMatch);
          return;
        }
        
        // Try partial match (Gemini might have shortened the tag)
        const partialMatch = tags.find(tag => 
          tag.toLowerCase().includes(geminiTag.toLowerCase()) ||
          geminiTag.toLowerCase().includes(tag.toLowerCase())
        );
        if (partialMatch) {
          geminiToRequestedMap.set(geminiTag, partialMatch);
          return;
        }
      });
      
      // Filter and map to correct tag names
      const filteredMappings = categorized
        .filter((item: any) => geminiToRequestedMap.has(item.tag))
        .map((item: any) => ({
          ...item,
          tag: geminiToRequestedMap.get(item.tag) // Use the original requested tag name
        }));
      
      
      // Create pending mappings
      const pendingMappings: PendingTagMapping[] = filteredMappings.map((item: any) => ({
        tag: item.tag,
        category: item.category as TagCategory,
        confidence: item.confidence || 0.8,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        source: 'gemini' as const,
        status: 'pending' as const
      }));
      
      // If we didn't get all the expected mappings, create fallbacks for missing ones
      const mappedTagNames = new Set(pendingMappings.map(m => m.tag));
      const missingTags = tags.filter(tag => !mappedTagNames.has(tag));
      
      if (missingTags.length > 0) {
        const fallbackMappings: PendingTagMapping[] = missingTags.map(tag => ({
          tag,
          category: 'moods' as TagCategory,
          confidence: 0.5,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          source: 'gemini' as const,
          status: 'pending' as const
        }));
        pendingMappings.push(...fallbackMappings);
      }
      
      return pendingMappings;
    } catch (error) {
      console.error('Error processing batch:', error);
      // Return fallback mappings rather than failing completely
      return tags.map(tag => ({
        tag,
        category: 'moods' as TagCategory,
        confidence: 0.3,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        source: 'gemini' as const,
        status: 'pending' as const
      }));
    }
  }

  // Save pending mappings for review
  async savePendingMappings(mappings: PendingTagMapping[]): Promise<void> {
    const batch = mappings.map(mapping => {
      // Reference the subcollection: mappings/config/pendingTagMappings
      const docRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION, mapping.tag);
      return setDoc(docRef, mapping);
    });
    
    await Promise.all(batch);
  }

  // Get all pending mappings for review
  async getPendingMappings(): Promise<PendingTagMapping[]> {
    const pendingRef = collection(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION);
    const q = query(pendingRef, where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    
return snapshot.docs.map(doc => doc.data() as PendingTagMapping);
  }

  // Approve mappings and move to main collection
  async approveMappings(mappings: PendingTagMapping[], reviewerId: string): Promise<void> {
    const now = Timestamp.now();
    
    const approvalPromises = mappings.map(async (mapping) => {
      // Update the mapping with review info
      const approvedMapping: TagMapping = {
        ...mapping,
        reviewedBy: reviewerId,
        reviewedAt: now,
        updatedAt: now,
        source: mapping.source
      };
      
      // Save to main subcollection
      const mainDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION, mapping.tag);
      await setDoc(mainDocRef, approvedMapping);
      
      // Update status in pending subcollection
      const pendingDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION, mapping.tag);
      await setDoc(pendingDocRef, { ...mapping, status: 'approved' }, { merge: true });
      
      // Update cache
      this.cache.set(mapping.tag.toLowerCase(), mapping.category);
    });
    
    await Promise.all(approvalPromises);
  }

  // Get all existing approved mappings
  async getAllMappings(): Promise<TagMapping[]> {
    const mappingsRef = collection(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION);
    const q = query(mappingsRef, orderBy('tag'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      ...doc.data(),
      tag: doc.id
    } as TagMapping));
  }

  // Update a specific mapping
  async updateMapping(tag: string, newCategory: TagCategory, reviewerId: string): Promise<void> {
    const now = Timestamp.now();
    
    const updatedMapping: TagMapping = {
      tag,
      category: newCategory,
      reviewedBy: reviewerId,
      reviewedAt: now,
      createdAt: now,
      updatedAt: now,
      source: 'manual'
    };
    
    const docRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION, tag);
    await setDoc(docRef, updatedMapping);
    
    // Update cache
    this.cache.set(tag.toLowerCase(), newCategory);
  }

  // Update multiple mappings at once
  async updateMappings(updates: Array<{ tag: string; category: TagCategory }>, reviewerId: string): Promise<void> {
    const now = Timestamp.now();
    
    const updatePromises = updates.map(async ({ tag, category }) => {
      const updatedMapping: TagMapping = {
        tag,
        category,
        reviewedBy: reviewerId,
        reviewedAt: now,
        createdAt: now,
        updatedAt: now,
        source: 'manual'
      };
      
      const docRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION, tag);
      await setDoc(docRef, updatedMapping);
      
      // Update cache
      this.cache.set(tag.toLowerCase(), category);
    });
    
    await Promise.all(updatePromises);
  }

  // Delete a tag mapping
  async deleteMapping(tag: string): Promise<void> {
    // Delete from main mappings collection
    const docRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION, tag);
    await deleteDoc(docRef);
    
    // Also delete from pending mappings if it exists
    const pendingDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION, tag);
    await deleteDoc(pendingDocRef).catch(() => {
      // Ignore error if pending mapping doesn't exist
    });
    
    // Remove from cache
    this.cache.delete(tag.toLowerCase());
  }

  // Delete multiple mappings at once
  async deleteMappings(tags: string[]): Promise<void> {
    const deletePromises = tags.map(tag => this.deleteMapping(tag));
    await Promise.all(deletePromises);
  }

  // Find tags that exist in tracks OR tag collection but not in mappings
  async findUnmappedTags(): Promise<string[]> {
    const allTags = new Set<string>();
    
    // 1. Get all unique tags from audio metadata (used tags)
    const tracksRef = collection(this.firebase.db, 'audiometa');
    const tracksSnapshot = await getDocs(tracksRef);
    
    // let docsWithTags = 0;
    
    tracksSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.tags && Array.isArray(data.tags)) {
        data.tags.forEach((tagObj: any) => {
          // Tags are stored as objects with 'label' and 'value' properties
          if (tagObj && tagObj.label && typeof tagObj.label === 'string') {
            const tagLabel = tagObj.label.trim();
            if (tagLabel) {
              allTags.add(tagLabel);
            }
          }
        });
      }
    });
    
    // 2. Get all tags from tags collection (including unused ones)
    const tagsRef = collection(this.firebase.db, 'tags');
    const tagsSnapshot = await getDocs(tagsRef);
    
    tagsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.label && typeof data.label === 'string') {
        const tagLabel = data.label.trim();
        if (tagLabel) {
          allTags.add(tagLabel);
        }
      }
    });
    
    // Get all mapped tags
    const mappingsRef = collection(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION);
    const mappingsSnapshot = await getDocs(mappingsRef);
    
    const mappedTags = new Set<string>();
    mappingsSnapshot.forEach(doc => {
      mappedTags.add(doc.id);
    });
    
    // Find unmapped tags
    return Array.from(allTags).filter(tag => !mappedTags.has(tag));
  }

  // Process new tags: find, categorize with Gemini, and save as pending
  async processNewTags(): Promise<{ count: number; tags: string[] }> {
    // First clean up any orphaned pending mappings
    await this.cleanupOrphanedPendingMappings();
    
    const unmappedTags = await this.findUnmappedTags();
    
    if (unmappedTags.length === 0) {
      return { count: 0, tags: [] };
    }
    
    // Categorize with Gemini
    const pendingMappings = await this.categorizeWithGemini(unmappedTags);
    
    // Save as pending for review
    await this.savePendingMappings(pendingMappings);
    
    return { count: unmappedTags.length, tags: unmappedTags };
  }

  // Get statistics about mappings
  async getMappingStats(): Promise<{
    total: number;
    byCategory: Record<TagCategory, number>;
    pending: number;
  }> {
    await this.loadMappingsToCache();
    
    const byCategory: Record<TagCategory, number> = {
      moods: 0,
      instruments: 0,
      energy: 0,
      period: 0,
      style: 0,
      location: 0,
      other: 0
    };
    
    this.cache.forEach(category => {
      byCategory[category]++;
    });
    
    const pendingCount = (await this.getPendingMappings()).length;
    
    return {
      total: this.cache.size,
      byCategory,
      pending: pendingCount
    };
  }

  // Delete tag mapping when a tag is deleted from Fields
  async deleteTagMapping(tagLabel: string): Promise<void> {
    try {
      
      // Delete from main mappings collection
      const mainDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.TAG_MAPPINGS_SUBCOLLECTION, tagLabel);
      await deleteDoc(mainDocRef);
      
      // Delete from pending mappings collection if exists
      const pendingDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION, tagLabel);
      await deleteDoc(pendingDocRef);
      
      // Remove from cache
      this.cache.delete(tagLabel.toLowerCase());
    } catch (error) {
      console.error(`Error deleting tag mapping for "${tagLabel}":`, error);
      // Don't throw error to prevent breaking the main field deletion
    }
  }

  // Delete multiple tag mappings at once
  async deleteTagMappings(tagLabels: string[]): Promise<void> {
    const deletePromises = tagLabels.map(tagLabel => this.deleteTagMapping(tagLabel));
    await Promise.allSettled(deletePromises); // Use allSettled to not fail on individual errors
  }

  // Clean up orphaned pending mappings (mappings for tags that are already mapped OR don't exist)
  async cleanupOrphanedPendingMappings(): Promise<void> {
    try {
      // Get all pending mappings
      const pendingMappings = await this.getPendingMappings();
      
      if (pendingMappings.length === 0) {
        return;
      }
      
      // Get all current mappings
      await this.loadMappingsToCache(true);
      
      // Get all actual tags that exist in the system
      const allTags = new Set<string>();
      
      // Get tags from audiometa
      const tracksRef = collection(this.firebase.db, 'audiometa');
      const tracksSnapshot = await getDocs(tracksRef);
      tracksSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.tags && Array.isArray(data.tags)) {
          data.tags.forEach((tagObj: any) => {
            if (tagObj && tagObj.label && typeof tagObj.label === 'string') {
              allTags.add(tagObj.label.trim());
            }
          });
        }
      });
      
      // Get tags from tags collection
      const tagsRef = collection(this.firebase.db, 'tags');
      const tagsSnapshot = await getDocs(tagsRef);
      tagsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.label && typeof data.label === 'string') {
          allTags.add(data.label.trim());
        }
      });
      
      // Find pending mappings that are either:
      // 1. Already mapped, OR
      // 2. For tags that don't actually exist in the system
      const orphanedMappings = pendingMappings.filter(pending => {
        const isAlreadyMapped = this.cache.has(pending.tag.toLowerCase());
        const tagDoesntExist = !allTags.has(pending.tag);
        return isAlreadyMapped || tagDoesntExist;
      });
      
      if (orphanedMappings.length > 0) {
        const deletePromises = orphanedMappings.map(mapping => {
          const pendingDocRef = doc(this.firebase.db, this.MAPPINGS_COLLECTION, 'config', this.PENDING_MAPPINGS_SUBCOLLECTION, mapping.tag);
          return deleteDoc(pendingDocRef);
        });
        
        await Promise.allSettled(deletePromises);
      }
    } catch (error) {
      console.error('Error cleaning up orphaned pending mappings:', error);
    }
  }
}

export const tagMappingService = new TagMappingService();